import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatGameTime } from "../../format/formatGameTime";
import { formatList } from "../../format/formatList";
import type { LaneSummary } from "../../model/Analysis";
import type { FindingSeverity } from "../../model/Finding";
import { AssetIcon } from "../AssetIcon";

const VERSUS = msg({ id: "analysis.lane.versus", message: "vs" });
const WON = msg({ id: "analysis.lane.won", message: "Lane won" });
const LOST = msg({ id: "analysis.lane.lost", message: "Lane lost" });
const EVEN = msg({ id: "analysis.lane.even", message: "Even lane" });
const FIRST = msg({
  id: "analysis.lane.first",
  message:
    "By {time} you had {souls, plural, one {# soul} other {# souls}}, {enemies} — {laneSouls, number} on average.",
});
const LATER = msg({
  id: "analysis.lane.later",
  message: "By {time}: {souls, number} vs {laneSouls, number}.",
});

type LaneCardProps = {
  lane: LaneSummary;
  heroId: number;
  // Оценка линии из находки `lane_result`; `null` — линия ровная, находки нет.
  outcome: FindingSeverity | null;
};

// Линия игрока: кто стоял рядом и против кого, души против соперников в конце фазы линий
// и позже. Оценку даёт находка `lane_result` с порогами бэкенда, сама карточка не судит.
export const LaneCard = ({ lane, heroId, outcome }: LaneCardProps) => {
  const { _, i18n } = useLingui();
  const findHero = useAssetLookup("heroes");
  const [first, ...later] = lane.points;
  if (first === undefined) {
    return null;
  }
  const enemies = lane.enemy_hero_ids.map(findHero);
  const verdict = outcome === "good" ? WON : outcome === null ? EVEN : LOST;
  return (
    <div
      data-lane={outcome ?? "even"}
      className="flex flex-wrap items-center gap-4 rounded-[10px] bg-surface-raised px-4 py-3.5"
    >
      <div className="flex items-center gap-1.5">
        {[heroId, ...lane.ally_hero_ids].map((id) => (
          <AssetIcon key={`ally-${id}`} asset={findHero(id)} size={36} className="rounded-lg" />
        ))}
        <span className="px-1 text-[13px] text-subtle">{_(VERSUS)}</span>
        {enemies.map((hero) => (
          <AssetIcon key={`enemy-${hero.id}`} asset={hero} size={36} className="rounded-lg" />
        ))}
      </div>
      <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-0.5">
        <span className="font-semibold text-foreground">{_(verdict)}</span>
        <span className="text-sm text-foreground-soft">
          {_({
            ...FIRST,
            values: {
              time: formatGameTime(first.time_s),
              souls: first.souls,
              laneSouls: first.lane_souls,
              enemies: formatList(
                enemies.map((hero) => hero.name),
                i18n.locale,
              ),
            },
          })}
          {later.map((point) => (
            <span key={point.time_s}>
              {" "}
              {_({
                ...LATER,
                values: {
                  time: formatGameTime(point.time_s),
                  souls: point.souls,
                  laneSouls: point.lane_souls,
                },
              })}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
};
